import pick from 'lodash/pick';
import { useState } from 'react';
import styled from 'styled-components';

import { BodilColor } from '~src/branding';
import { useBranding } from '~src/context/BrandingContext';
import { useSelectedProductTypesContext } from '~src/context/selectedProductTypeContext';
import { useSavingsEstimate } from '~src/hooks/useSavings';
import { Offer, ProductTypes } from '~src/model';

import { Icon } from '../Icon';
import { InfoBox, InfoBoxNorlys } from '../InfoBox';
import { Tab } from './components';
import { SavingsWidgetProps } from './types';
import { showTakeover } from './util';

const Wrapper = styled.div({
  width: 'fit-content',
});

const TabContainer = styled.div({
  display: 'flex',
  gap: '8px',
  paddingLeft: '32px',
  marginBottom: -4,
});

export const SavingsWidget = (inputProps: SavingsWidgetProps) => {
  const { style, wrapperStyle } = inputProps;

  const { entries } = useSavingsEstimate(inputProps);

  const props = {
    ...inputProps,
    entries: entries.map(entry => ({ ...entry, valueLevel: 1 })),
    alignHeader: 'center',
    style: { ...style, ...wrapperStyle, height: 'unset', padding: '24px 0' },
  };

  return <InfoBox {...props} />;
};

export const SavingsWidgetNorlys = (inputProps: Omit<SavingsWidgetProps, 'type'>) => {
  const { entries } = useSavingsEstimate(inputProps);

  const props = { ...inputProps, entries, style: { ...inputProps.style, ...inputProps.wrapperStyle } };

  return (
    <div>
      <InfoBoxNorlys {...props} />
    </div>
  );
};

export const SavingsWidgetWithTabs = (props: Omit<SavingsWidgetProps, 'type'>) => {
  const { selectedProductTypes, setSelectedProductTypes } = useSelectedProductTypesContext();

  const { products, takeover, style, wrapperStyle } = props;

  const [activeTabs, setActiveTabs] = useState<ProductTypes>(selectedProductTypes);
  const { lightColor } = useBranding();

  const updateActiveTabs = (types: ProductTypes) => {
    setSelectedProductTypes(types);
    setActiveTabs(types);
  };

  const iconProps = { color: BodilColor.base.white };

  return (
    <Wrapper style={wrapperStyle}>
      <TabContainer>
        <Tab
          isActive={activeTabs.length === 1 && activeTabs.includes('heatPump')}
          onClick={() => updateActiveTabs(['heatPump'])}
        >
          <Icon.HeatPump {...iconProps} />
        </Tab>

        <Tab
          isActive={activeTabs.length === 1 && activeTabs.includes('solar')}
          onClick={() => updateActiveTabs(['solar'])}
        >
          <Icon.Solar {...iconProps} />
        </Tab>

        <Tab isActive={activeTabs.length === 2} onClick={() => updateActiveTabs(['solar', 'heatPump'])}>
          <div style={{ display: 'flex', gap: '4px', color: 'white' }}>
            <Icon.HeatPump {...iconProps} />
            +
            <Icon.Solar {...iconProps} />
          </div>
        </Tab>
      </TabContainer>

      <SavingsWidget
        {...{
          ...props,
          productType: activeTabs,
          takeover: showTakeover(activeTabs, takeover),
          style: {
            ...style,
            position: 'relative',
            border: `${'4px'} solid ${lightColor}`,
          },
          ...getOfferAndTitle(activeTabs, products),
        }}
      />
    </Wrapper>
  );
};

const getOfferAndTitle = (selectedProductTypes: ProductTypes, products?: Partial<Offer>) => {
  if (selectedProductTypes.length === 2) {
    return { products, title: 'Varmepumpe og solceller' };
  }

  if (selectedProductTypes.includes('heatPump')) {
    return { products: pick(products, ['heatPumpOutdoorUnit', 'heatPumpIndoorUnit']), title: 'Varmepumpe' };
  }

  return { products: pick(products, ['solarPanel', 'battery', 'inverter']), title: 'Solceller' };
};
